// The practice's Cherry PLAN CATALOG, as Settings shows it.
//
// Cherry has no "list my plans" endpoint, so cherry-service prices a fixed ladder of amounts and
// merges the answers into one catalog row per plan (see docs/plans/2026-10-16-cherry-plan-catalog-plan.md):
//
//   catalog = { plans: [{ key, term, periodLength, aprMin, aprMax, minAmount, maxAmount }],
//               ladder: [250, 500, ...], fetchedAt: ISO }
//
// Everything here is DISPLAY wording for the Settings -> "Your Cherry plans" table. It has no PDF
// twin: the signed PDF and the patient surfaces draw Cherry's own priced rows (cherryTerms.js),
// never this catalog. Pure and DOM-free so node-tests can execute this exact file.
//
// Imports from cherryTerms.js (never the reverse - that file must stay import-free).
import { cherryPlanKey, normalizeHiddenPlanKeys } from './cherryTerms.js';

/** A catalog older than this is refreshed automatically when Settings opens. */
export const CATALOG_STALE_MS = 7 * 24 * 60 * 60 * 1000;

/** Is there no catalog worth trusting? True when absent, undated, unparseable or older than 7 days. */
export function isCatalogStale(catalog, now) {
  const iso = catalog && catalog.fetchedAt;
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return true;
  return (now === undefined ? Date.now() : now) - t > CATALOG_STALE_MS;
}

/**
 * The plan's name in the table.
 *   MONTHLY  term 12 -> "12 months"      term 1 -> "1 month"
 *   BIWEEKLY term 3  -> "3 payments every 2 weeks"
 *   WEEKLY   term 8  -> "8 payments every week"
 * ⛔ Cherry's `term` EXCLUDES the down payment (TOTAL payments = term + 1). This is the plan's
 * NAME as Cherry's own portal words it; the priced rows the patient sees (cherryTierRows) state
 * the real payment count ("13 payments total") beside every figure.
 */
export function cherryPlanTitle(plan) {
  const term = parseInt(plan && plan.term, 10) || 0;
  const cadence = String((plan && plan.periodLength) || 'MONTHLY').toUpperCase();
  if (cadence === 'MONTHLY') return `${term} month${term === 1 ? '' : 's'}`;
  const noun = `payment${term === 1 ? '' : 's'}`;
  if (cadence === 'BIWEEKLY') return `${term} ${noun} every 2 weeks`;
  if (cadence === 'WEEKLY') return `${term} ${noun} every week`;
  return `${term} ${noun} (${cadence.toLowerCase()})`;
}

// Number(null) is 0 and Number('') is 0 - a missing figure must read as MISSING, not as zero.
function finiteOrNaN(n) {
  return n === null || n === undefined || n === '' ? NaN : Number(n);
}

function pct(n) {
  const v = finiteOrNaN(n);
  return Number.isFinite(v) ? `${v}%` : '';
}

/** "0%", "17.8%", or a range "17.8%–19.8%" when the rate varied across the amounts priced. '—' if unknown. */
export function cherryAprText(plan) {
  const lo = pct(plan && plan.aprMin);
  const hi = pct(plan && plan.aprMax);
  if (!lo && !hi) return '—';
  if (!lo || !hi || lo === hi) return lo || hi;
  return `${lo}–${hi}`;
}

function wholeDollars(n) {
  return '$' + Math.round(Number(n)).toLocaleString('en-US');
}

/**
 * Where in the dollar range this plan applies, worded loosely ON PURPOSE: boundaries are only
 * known to the ladder's resolution (a plan seen at $2,500 but not at $3,500 ends somewhere
 * between), so it says "about", and the Builder prices the real amount anyway.
 *
 *   minAmount above the ladder's smallest point -> "From about $1,000"
 *   maxAmount below the ladder's largest point  -> "Up to about $2,500"
 *   both                                        -> "About $1,000 – $2,500"
 *   offered across the whole ladder             -> ''
 */
export function cherryAvailabilityNote(plan, ladder) {
  const points = (Array.isArray(ladder) ? ladder : []).map(finiteOrNaN).filter((n) => Number.isFinite(n));
  if (!plan || !points.length) return '';
  const smallest = Math.min.apply(null, points);
  const largest = Math.max.apply(null, points);
  const min = finiteOrNaN(plan.minAmount);
  const max = finiteOrNaN(plan.maxAmount);
  const from = Number.isFinite(min) && min > smallest;
  const upTo = Number.isFinite(max) && max < largest;
  if (from && upTo) return `About ${wholeDollars(min)} – ${wholeDollars(max)}`;
  if (from) return `From about ${wholeDollars(min)}`;
  if (upTo) return `Up to about ${wholeDollars(max)}`;
  return '';
}

/**
 * The table rows for a catalog, in the backend's order (MONTHLY by term, then other cadences):
 *   { key, title, apr, availability, offered }
 * `offered` is false when the key is in `hiddenList` (the option's `cherryHiddenPlans`). The key
 * is REBUILT here with cherryPlanKey (term + cadence) rather than read from the catalog's own
 * `key` field, so the table, the hide list and every renderer can never disagree about what
 * "12-MONTHLY" means. A row with no usable term is not a plan and is dropped.
 */
export function cherryCatalogRows(catalog, hiddenList) {
  const hidden = normalizeHiddenPlanKeys(hiddenList);
  const plans = catalog && Array.isArray(catalog.plans) ? catalog.plans : [];
  const rows = [];
  plans.forEach((p) => {
    if (!p || typeof p !== 'object') return;
    const key = cherryPlanKey(p);
    if (key.indexOf('0-') === 0) return; // no term - not a plan
    rows.push({
      key,
      title: cherryPlanTitle(p),
      apr: cherryAprText(p),
      availability: cherryAvailabilityNote(p, catalog.ladder),
      offered: hidden.indexOf(key) === -1,
    });
  });
  return rows;
}

/**
 * The next hide list after the practice ticks / unticks "Offer to patients" on one plan:
 * normalised, de-duplicated, others untouched. Unticking adds the key, ticking removes it.
 */
export function toggleHiddenPlanKey(list, key, offered) {
  const k = String(key || '').trim().toUpperCase();
  const next = normalizeHiddenPlanKeys(list).filter((x) => x !== k);
  if (!offered && k) next.push(k);
  return next;
}

export default {
  CATALOG_STALE_MS,
  isCatalogStale,
  cherryPlanTitle,
  cherryAprText,
  cherryAvailabilityNote,
  cherryCatalogRows,
  toggleHiddenPlanKey,
};
